import { canAccessModule } from '../utils/permissionUtils';

/**
 * The search box: how each kind of result the server sends (GlobalSearchService, `GET /api/search?q=`) is headed and where it goes. WHICH
 * kinds a person is given is decided by the server from their role; here a kind is also dropped when this person can't open its screen, so a
 * result never leads to a refusal, and a kind this version does not know is not shown. The screen opens with `?find=<number or name>`,
 * which scrolls to the row and marks it (hooks/useFindOnPage.js).
 *
 *   label   the group heading     icon   its icon     route(user)   the screen for this person, or null for none
 */
const isAdmin = (user) => (user?.roles || []).some((r) => ['ROLE_ORG_ADMIN', 'ROLE_ADMIN', 'ROLE_SYSTEM_ADMIN'].includes(typeof r === 'string' ? r : r?.name));

export const SEARCH_TYPES = {
  customer: { label: 'Customers', icon: 'bi-person', route: (u) => (canAccessModule(u, 'sales') ? '/sales/customers' : null) },
  vendor: { label: 'Vendors', icon: 'bi-truck', route: (u) => (canAccessModule(u, 'purchase') ? '/purchase/vendors' : null) },
  'sale-bill': { label: 'Sale bills', icon: 'bi-receipt', route: (u) => (canAccessModule(u, 'sales') ? '/sales/salebills' : null) },
  quotation: { label: 'Quotations', icon: 'bi-file-earmark-text', route: (u) => (canAccessModule(u, 'sales') ? '/sales/quotations' : null) },
  'purchase-order': { label: 'Purchase orders', icon: 'bi-cart', route: (u) => (canAccessModule(u, 'purchase') ? '/purchase/purchase-orders' : null) },
  'purchase-bill': { label: 'Purchase bills', icon: 'bi-journal-text', route: (u) => (canAccessModule(u, 'purchase') ? '/purchase/purchase-invoices' : null) },
  grn: { label: 'Goods receipts', icon: 'bi-box-arrow-in-down', route: (u) => (canAccessModule(u, 'inventory') ? '/inventory/grns' : null) },
  gin: { label: 'Goods issues', icon: 'bi-box-arrow-up', route: (u) => (canAccessModule(u, 'inventory') ? '/inventory/gins' : null) },
  // Materials and products are listed in Org Admin for an admin; anyone else is shown the stock of it
  material: {
    label: 'Materials',
    icon: 'bi-box-seam',
    route: (u) => {
      if (isAdmin(u) && canAccessModule(u, 'org-admin')) return '/org-admin/organization-materials';
      return canAccessModule(u, 'inventory') ? '/inventory/stock-levels' : null;
    }
  },
  product: {
    label: 'Products',
    icon: 'bi-tags',
    route: (u) => {
      if (isAdmin(u) && canAccessModule(u, 'org-admin')) return '/org-admin/products';
      if (canAccessModule(u, 'sales')) return '/sales/catalog';
      return canAccessModule(u, 'inventory') ? '/inventory/stock-levels' : null;
    }
  }
};

/** The server's groups, headed and linked for this person; empty groups and kinds they can't open are left out. */
export function describeGroups(serverGroups, user) {
  const out = [];
  (serverGroups || []).forEach((g) => {
    const def = SEARCH_TYPES[g.type];
    const path = def && def.route(user);
    if (!path) return;
    const items = (g.results || []).map((r) => ({
      key: `${g.type}-${r.id}`,
      title: r.title,
      subtitle: r.subtitle || '',
      date: r.date || '',
      status: r.status || '',
      to: `${path}?find=${encodeURIComponent(r.find || r.title)}`
    }));
    if (items.length) out.push({ type: g.type, label: def.label, icon: def.icon, items });
  });
  return out;
}
